import React from "react";
import useAccess from "../../hooks/useAccess.jsx";
import useConsolidationCalendar from "../../hooks/useConsolidationCalendar.js";
import { fetchBillableItemSummary, fetchInvoiceSummary } from "../../api/billingApi.js";
import { getDispatchMonthKey, getLocalTodayDispatchDate } from "../../utils/dispatchDayCalendarUtils.js";

// Habillage commun à la Consolidation et à la Facturation : bande ardoise, onglets
// et bandeau d'indicateurs. Une seule palette d'états pour les deux sections.
export const STATE_TONES = {
  todo: "#64748b", progress: "#a86a24", issued: "#3f5585", done: "var(--color-accent)", blocked: "#c5524a", void: "#94a3b8",
};
const INK = "#1f2b35";
const MONEY = new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" });
const MONTH = new Intl.DateTimeFormat("fr-FR", { month: "long" });

export function StateSquare({ tone }) {
  return tone ? <i aria-hidden="true" className="inline-block h-2 w-2 shrink-0" style={{ background: STATE_TONES[tone] || tone }} /> : null;
}

// Chiffres du résumé de l’autre section, lus en arrière-plan.
function useBillingCounts(enabled, refreshKey) {
  const [counts, setCounts] = React.useState(null);
  React.useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    Promise.all([fetchBillableItemSummary({}, { signal: controller.signal }), fetchInvoiceSummary({}, { signal: controller.signal })])
      .then(([items, invoices]) => setCounts({ items, invoices }))
      .catch(() => { if (!controller.signal.aborted) setCounts(null); });
    return () => controller.abort();
  }, [enabled, refreshKey]);
  return counts;
}

// Le mois est lourd (≈ 3 s) : il garde son propre cache et ne suit pas les rechargements de la page.
function useConsolidationCounts(enabled, month) {
  const { data } = useConsolidationCalendar(month, enabled, undefined, false);
  if (!data) return null;
  const today = getLocalTodayDispatchDate();
  // Seulement les journées dont le Dispatch est clos, comme le panneau « Jours » du Dispatch.
  const days = data.days.filter((day) => day.date <= today && day.dispatch_state !== "open");
  return { pending: days.filter((day) => day.status === "pending").length, started: days.filter((day) => day.status === "in_progress").length };
}

// Résumé non cliquable de l'autre section, à côté des boutons de la page.
export function WorkspaceSummary({ section, month = getDispatchMonthKey(getLocalTodayDispatchDate()), refreshKey }) {
  const { hasPermission } = useAccess();
  const lines = useStepLines(section, hasPermission(PERMISSIONS[section]), month, refreshKey);
  if (!lines) return null;
  const [first, second] = Object.values(lines);
  return <p className="flex items-center gap-1 whitespace-nowrap text-xs text-slate-600 [&_b]:text-slate-900">{TITLES[section]} : {first} · {second}</p>;
}

const TITLES = { consolidation: "Consolidation", billing: "Facturation" };
const PERMISSIONS = { consolidation: "billing.consolidation.read", billing: "billing.read" };

// Le résumé omet « à envoyer » quand aucun relevé n’attend son envoi.
function useStepLines(section, enabled, month, refreshKey) {
  const consolidation = useConsolidationCounts(enabled && section === "consolidation", month);
  const billing = useBillingCounts(enabled && section === "billing", refreshKey);
  const plural = (count, word) => <><b>{count}</b> {word}{count > 1 ? "s" : ""}</>;
  if (consolidation) {
    const monthLabel = MONTH.format(new Date(`${month}-01T12:00:00`));
    return {
      1: <><StateSquare tone="todo" />{plural(consolidation.pending, "journée")} à commencer en {monthLabel}</>,
      2: <><StateSquare tone="progress" /><b>{consolidation.started}</b> en cours</>,
    };
  }
  if (!billing) return null;
  const issued = Number(billing.invoices.status_counts?.issued) || 0;
  return {
    3: <><StateSquare tone="done" />{MONEY.format(Number(billing.items.ready_amount) || 0)} à facturer</>,
    4: <><StateSquare tone="progress" />{plural(Number(billing.invoices.drafts?.count) || 0, "brouillon")}{!issued ? null : <> · <b>{issued}</b> à envoyer</>}</>,
  };
}

// titleAs : « p » quand le titre de niveau 1 est ailleurs (la période en Consolidation).
export function WorkspaceBand({ icon: Icon, title, titleAs: Title = "h1", subtitle, tabs, tabsRight, tabsSlot, label = title, tabsLabel = `Vues de ${label.toLowerCase()}` }) {
  return <section aria-label={label} className="relative isolate min-w-0 overflow-hidden rounded-[4px] border border-[#1f2b35] bg-white">
    <div className="relative isolate overflow-hidden" style={{ background: INK }}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 -z-10 w-[46%] max-w-[620px] opacity-35 max-lg:hidden"
        style={{ backgroundImage: "url('/static/react/brand/reports/transia-grain-banner.png')", backgroundPosition: "right center", backgroundSize: "cover",
          maskImage: "linear-gradient(90deg, transparent 0%, rgb(0 0 0 / 0.25) 20%, rgb(0 0 0 / 0.9) 46%, black 55%)" }} />
      <div className="min-w-0 px-3 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] bg-accent text-white"><Icon size={19} /></span>
          <div className="min-w-0">
            <Title className="text-2xl font-bold leading-7 tracking-tight text-white">{title}</Title>
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5 text-[13px] leading-5 text-[#cbd5e1]">{subtitle}</div>
          </div>
        </div>
      </div>
    </div>
    <div className="flex min-w-0 flex-wrap items-end gap-x-1 border-t border-slate-200 bg-slate-100 px-3">
      <nav className="flex min-w-0 max-w-full gap-0.5 overflow-x-auto [scrollbar-width:none]" aria-label={tabsLabel}>{tabs}</nav>
      {tabsSlot ? <div ref={tabsSlot} className="flex min-w-0 flex-[0_1_auto] self-center pl-3 empty:hidden" /> : null}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2 self-center py-1.5 empty:py-0">{tabsRight}</div>
    </div>
  </section>;
}

// Onglet commun, souligné en vert quand il est choisi pour ne pas ressembler aux boutons d'action ;
// le compteur « reste à faire » est ocre plein.
export function WorkspaceTab({ active, count, icon: Icon, children, ...props }) {
  return <button type="button" aria-pressed={active} {...props}
    className={`inline-flex h-[42px] items-center gap-[7px] whitespace-nowrap border-b-[3px] px-4 text-sm max-sm:px-2.5 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-45 ${active
      ? "border-accent bg-white font-bold text-accent" : "border-transparent font-semibold text-slate-600 hover:bg-slate-200 hover:text-slate-900"}`}>
    {Icon ? <Icon size={16} aria-hidden="true" className="shrink-0" /> : null}
    {children}{count ? <span title="Reste à faire" className="rounded-[4px] bg-[#a86a24] px-[5px] text-[10.5px] text-white tabular-nums">{count}</span> : null}
  </button>;
}

// Bandeau de cellules jointes, comme le Tableau de bord.
export function WorkspaceKpis({ label, children, className = "" }) {
  return <div role="group" aria-label={label} className={`grid min-w-0 shrink-0 auto-cols-fr overflow-hidden rounded-[4px] border border-[#dfe5e2] bg-white sm:grid-flow-col max-sm:grid-cols-2 ${className}`}>{children}</div>;
}

export function WorkspaceKpi({ tone, icon: Icon, label, value, sub, active = false, onClick, disabled = false, valueClassName = "text-slate-900", compact = false }) {
  const caption = <span className={`flex items-center gap-1.5 text-[11px] text-slate-700 ${compact ? `col-start-2 row-start-1 ${sub ? "" : "row-span-2"}` : ""}`}>{Icon ? <Icon size={12} aria-hidden="true" className="shrink-0" style={{ color: STATE_TONES[tone] || tone }} /> : <StateSquare tone={tone} />}{label}</span>;
  const amount = <strong className={`block text-[20px] font-[750] leading-6 tabular-nums ${compact ? "col-start-1 row-start-1 row-span-2 whitespace-nowrap" : ""} ${valueClassName}`}>{value}</strong>;
  const detail = sub ? <span className={`block text-[11.5px] leading-4 text-slate-500 ${compact ? "col-start-2 row-start-2" : ""}`}>{sub}</span> : null;
  const body = <>{caption}{amount}{detail}</>;
  const className = `min-w-0 border-l border-slate-200 px-[13px] text-left first:border-l-0 ${compact ? "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2.5 py-1.5" : "pb-2 pt-[7px]"} ${active ? "shadow-[inset_0_-3px_0_var(--color-accent)]" : ""}`;
  return onClick
    ? <button type="button" aria-pressed={active} disabled={disabled} onClick={onClick} className={`${className} hover:bg-slate-50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-50`}>{body}</button>
    : <div className={className}>{body}</div>;
}
